'use client';

import styled from '@emotion/styled';
import { formatFullDate } from '../../../../i18n/formatFullDate';
import {
	Css,
	Markdown,
	Responsive,
	ResponsivePicture,
	getResponsiveContentfulImageUrl,
	progressiveJpegOptions,
	ContentWrap,
} from '../../../../ui';
import { IPageLayoutProps } from '../../../shared/layout/page/PageLayout';
import { IPageEntry } from '../../../../contentful/types/IPage';
import { INewsItemEntry } from '../../../../contentful/types/INewsItemEntry';

import { MarkdownRenderContextProvider } from '../../../../components/common/markdown';
import GenericPage from '../../../app/pages/ui/GenericPage';
import { PageRoutes } from '../../../app/pages/domain/constants/PageRoutes';
import { SocialLinks } from '../../../../components/common/social-links/SocialLinks';
import { useGetCurrentUrl } from '../../../../utils/useGetCurrentUrl';
import { ISiteSectionEntry } from '../../../../contentful/types/ISiteSection';
import { PageSections } from '../../../content-sections/ui/PageSections';

export interface IProps extends IPageLayoutProps {
	page: IPageEntry;
	newsItem: INewsItemEntry;
	siteSection?: ISiteSectionEntry;
}

export default function NewsArticlePage({
	page,
	newsItem,
	siteSection,
}: IProps) {
	const { title, body, image, publicationDate, summary, sections } =
		newsItem.fields;
	const contentfulHeaderImageUrl = image?.fields.file.url;
	const headerImageUrl = contentfulHeaderImageUrl
		? getResponsiveContentfulImageUrl(
				contentfulHeaderImageUrl,
				progressiveJpegOptions,
		  )
		: undefined;

	const currentUrl = useGetCurrentUrl();

	return (
		<GenericPage
			title={title}
			backlink={PageRoutes.CorporateNewsroomNews}
			backlinkText="News"
			page={page}
			hideHeaderImage
			siteSection={siteSection}
		>
			<ContentWrap>
				<Wrapper>
					{summary && <StyledHeader>{summary}</StyledHeader>}
					{publicationDate && (
						<PublicationDate>
							{formatFullDate(
								publicationDate || newsItem.updatedAt,
								'MMM d, yyyy',
							)}
						</PublicationDate>
					)}
					<StyledResponsivePicture source={headerImageUrl} rounded />
					<MarkdownRenderContextProvider>
						<Markdown source={body} />
					</MarkdownRenderContextProvider>

					<SocialLinks variant="dark" shareLinkUrl={currentUrl} />
				</Wrapper>
			</ContentWrap>
			<PageSections sections={sections} />
		</GenericPage>
	);
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: ${Css.pxToRem(40)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		h5 {
			max-width: 792px;
		}
	}
`;

const StyledHeader = styled.h5`
	margin-bottom: ${Css.pxToRem(16)};
`;

const PublicationDate = styled.p`
	margin: 0;
`;

const StyledResponsivePicture = styled(ResponsivePicture)`
	margin-block: ${Css.pxToRem(16)};
`;
